import type {EmploymentsResponse} from '@rocket-ui/lk-api';
import {mapEmploymentsToWorkStatusConnections} from 'features/registration/model/employmentConnectionStates';
import type {RegistrationWorkStatus} from 'features/registration/model/types';
import {isWorkStatusConnectedAppearance} from 'features/registration/model/workStatusConnection';

/** Текст ошибки apply, если типы занятости исполнителя не подходят предложению. */
export const OFFER_EMPLOYMENT_MISMATCH_API_ERROR =
	'Типы занятости исполнителя не соответствуют типам занятости предложения';

const KIND_TO_STATUS: Record<string, RegistrationWorkStatus> = {
	selfemployed: 'self_employment',
	self_employment: 'self_employment',
	entrepreneur: 'individual_entrepreneur',
	individual_entrepreneur: 'individual_entrepreneur',
	civil_contract: 'individual',
	individual: 'individual',
};

const STATUS_ORDER: RegistrationWorkStatus[] = ['self_employment', 'individual_entrepreneur', 'individual'];

const STATUS_LABEL: Record<RegistrationWorkStatus, string> = {
	self_employment: 'самозанятых',
	individual_entrepreneur: 'индивидуальных предпринимателей',
	individual: 'физических лиц',
};

function collectRequiredStatuses(employmentKinds: string[] | null | undefined): RegistrationWorkStatus[] {
	const required = new Set<RegistrationWorkStatus>();

	for (const kind of employmentKinds ?? []) {
		const status = KIND_TO_STATUS[kind.trim().toLowerCase()];
		if (status) required.add(status);
	}

	return STATUS_ORDER.filter((status) => required.has(status));
}

function joinStatuses(labels: string[]): string {
	if (labels.length <= 1) return labels[0] ?? '';
	if (labels.length === 2) return `${labels[0]} и ${labels[1]}`;

	return `${labels.slice(0, -1).join(', ')} и ${labels[labels.length - 1]}`;
}

/** «Предложение только для самозанятых» / «…для самозанятых и индивидуальных предпринимателей». */
export function formatOfferEmploymentRestriction(employmentKinds: string[] | null | undefined): string | null {
	const required = collectRequiredStatuses(employmentKinds);
	if (required.length === 0) return null;

	return `Предложение только для ${joinStatuses(required.map((status) => STATUS_LABEL[status]))}`;
}

/** Любой профиль в `selfEmploymentProfiles` с `active: true` — исполнитель уже самозанятый на платформе. */
function hasActiveSelfEmploymentProfile(employment: EmploymentsResponse): boolean {
	return Boolean(employment.selfEmploymentProfiles?.some((profile) => profile.active === true));
}

/**
 * Сообщение, если ни один вид из `employmentKinds` не подключён в GET /employment.
 * Для `selfemployed` достаточно любого профиля с `active: true`, провайдер не важен.
 * Пустой список видов и неизвестный ответ employment не блокируют отклик.
 */
export function getOfferEmploymentMismatchMessage(
	employmentKinds: string[] | null | undefined,
	employment: EmploymentsResponse | null | undefined,
): string | null {
	const required = collectRequiredStatuses(employmentKinds);
	if (required.length === 0 || !employment) return null;

	const connections = mapEmploymentsToWorkStatusConnections(employment);
	const hasMatch = required.some((status) => {
		if (status === 'self_employment') return hasActiveSelfEmploymentProfile(employment);

		return isWorkStatusConnectedAppearance(connections[status]);
	});

	if (hasMatch) return null;

	return formatOfferEmploymentRestriction(employmentKinds);
}
